import {
  Autocomplete,
  Box,
  Grid,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import * as XLSX from "xlsx";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import Loader from "../../components/Molecules/Loader/Loader";
import FlexBetween from "../../components/FlexBetween";
import ButtonComponent from "../../components/Button.Component";
import useFetchLinkedServices from "../../hooks/useFetchLinkedServices";
import useFetchConnectionKey from "../../hooks/useFetchConnectionKey";
import base from "../../middlewares/interceptors";
import { getSftpListSchema } from "../../schemas";
import { getSftpListsUrl } from "../../services/constants";
import DataTable from "../../components/DataGrids/DataGrid";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { getAllConnectionKeys } from "../../services/connectionKeysService";
import { useToast } from "../../services/utils";

const SftpList: React.FC = () => {
  const [formData, setFormData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [tableData, setTableData] = useState<{ columns: any[]; rows: any[] }>({
    columns: [],
    rows: [],
  });
  const [connectionKeys, setConnectionKeys] = useState<any[]>([]);
  const { showToast } = useToast();

  const [linkedServicesData] = useFetchLinkedServices({
    setIsLoading,
    sub_type: "sftp",
  });

  const [hookConnectionKeys] = useFetchConnectionKey({
    filterKeys: formData?.linked_service?.connection_details?.connection_keys,
    setIsLoading,
  });

  const connectionKeysData =
    connectionKeys.length > 0 ? connectionKeys : hookConnectionKeys;

  const validateField = async (name: string, value: any) => {
    try {
      const partialFormData = { ...formData, [name]: value };
      await getSftpListSchema.validateAt(name, partialFormData);
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const handleFormData = (e: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    validateField(e.target.name, e.target.value);
  };

  const headerNameMap: { [key: string]: string } = {
    file_name: "File Name",
    name: "File Name",
    file_path: "File Path",
    path: "Path",
    remote_directory: "Remote Directory",
    remote_path: "Remote Path",
    size: "File Size (Bytes)",
    file_size: "File Size (Bytes)",
    blob_size: "File Size (Bytes)",
    last_modified: "Last Modified Date",
    last_modified_time: "Last Modified Date",
    modified_at: "Last Modified Date",
    modified_time: "Last Modified Date",
    blob_last_modified_time: "Last Modified Date",
    creation_time: "Creation Date",
    created_at: "Creation Date",
    blob_creation_time: "Creation Date",
    type: "File Type",
    file_type: "File Type",
    extension: "Extension",
  };

  const toTitleCase = (value: string) =>
    value
      .split("_")
      .map((segment) =>
        segment ? `${segment.charAt(0).toUpperCase()}${segment.slice(1)}` : "",
      )
      .join(" ");

  const formatCellValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return value;
  };

  const hasFileLikeKey = (item: any) => {
    return Object.keys(item || {}).some((key) =>
      /file|path|name|size|modified|created|directory|extension|type/i.test(
        key,
      ),
    );
  };

  const extractRows = (responseData: any) => {
    const candidates = [
      responseData,
      responseData?.data,
      responseData?.payload,
      responseData?.result,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }

      if (candidate && typeof candidate === "object") {
        for (const key of ["files", "items", "results", "rows", "data"]) {
          if (Array.isArray(candidate[key])) {
            return candidate[key];
          }
        }

        if (hasFileLikeKey(candidate)) {
          return [candidate];
        }
      }
    }

    return [];
  };

  const normalizeTableData = (responseData: any) => {
    const rawRows = extractRows(responseData).map((item: any) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        return item;
      }

      return { file_name: item };
    });

    if (!rawRows.length) {
      return { columns: [], rows: [] };
    }

    const discoveredKeys = Array.from(
      new Set<string>(rawRows.flatMap((row: any) => Object.keys(row || {}))),
    );
    const preferredOrder = [
      "file_name",
      "name",
      "file_path",
      "path",
      "remote_path",
      "remote_directory",
      "creation_time",
      "created_at",
      "blob_creation_time",
      "last_modified",
      "last_modified_time",
      "modified_at",
      "modified_time",
      "blob_last_modified_time",
      "size",
      "file_size",
      "blob_size",
      "type",
      "file_type",
      "extension",
    ];

    const orderedKeys = [
      ...preferredOrder.filter((key) => discoveredKeys.includes(key)),
      ...discoveredKeys.filter((key) => !preferredOrder.includes(key)),
    ];

    const columnDefinitions = orderedKeys.map((key) => ({
      key,
      field: key === "id" ? "record_id" : key,
      headerName: headerNameMap[key] || toTitleCase(key),
    }));

    const columns = [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        renderCell: () => <></>,
      },
      ...columnDefinitions.map((column) => ({
        field: column.field,
        headerName: column.headerName,
        minWidth: 150,
        flex: 1,
        renderCell: (params: any) => (
          <LimitChractersWithTooltip value={params?.value} />
        ),
      })),
    ];

    const rows = rawRows.map((row: any, index: number) => {
      const formattedRow: any = { id: index };

      columnDefinitions.forEach((column) => {
        formattedRow[column.field] = formatCellValue(row?.[column.key]);
      });

      return formattedRow;
    });

    return { columns, rows };
  };

  // Parse the Excel binary blob returned by the SFTP listing endpoint.
  // Uses the XLSX approach, then feeds the row objects
  // into normalizeTableData so SFTP-specific column naming is preserved.
  const parseSftpResponse = async (response: any) => {
    const arrayBuffer = await response.data.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    // sheet_to_json (no header:1) produces [{col: val, ...}, ...] objects
    const jsonRows: any[] = XLSX.utils.sheet_to_json(worksheet);
    return normalizeTableData(jsonRows);
  };

  const getErrorMessage = (error: any) => {
    return (
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      "Unable to fetch SFTP files. Please try again."
    );
  };

  const onSaveGetSftpList = async (e: any) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setErrors({});

      await getSftpListSchema.validate(formData, { abortEarly: false });

      const reqBody = {
        linked_service_code: formData?.linked_service?.code,
        connection_key: formData?.connection_key,
        file_name: formData?.file_name ?? "",
        file_path: formData?.file_path ?? "",
        output_location: formData?.output_location ?? "",
      };

      // REQUEST responseType: "blob" because the SFTP listing endpoint returns
      // a binary Excel file.
      // Without this, axios treats the binary stream as JSON, producing garbled
      // data that extractRows cannot parse, which triggers the false "no files"
      // warning toast even on a successful 200 response.
      const response = await base.post(getSftpListsUrl, reqBody, {
        responseType: "blob",
      });

      if (response && response.status >= 200 && response.status < 300) {
        const parsedTableData = await parseSftpResponse(response);
        setTableData(parsedTableData);

        if (!parsedTableData.rows.length) {
          showToast(
            "No files found for the selected remote directory.",
            "warning",
          );
        }
      }
    } catch (error: any) {
      if (error?.inner) {
        const newErrors: { [key: string]: string } = {};
        error.inner.forEach((item: { path: string; message: string }) => {
          newErrors[item.path] = item.message;
        });
        setErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));
      } else {
        setTableData({ columns: [], rows: [] });
        showToast(getErrorMessage(error), "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <Box>
        <form onSubmit={onSaveGetSftpList} autoComplete="off">
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header ">
              <h3>Get SFTP List</h3>
            </Box>
            <Grid container rowSpacing={2.5} columnSpacing={4}>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">
                  <span className="position-relative">
                    Connection Type<span className="required-asterisk">*</span>
                    {formData?.linked_service?.id && (
                      <Tooltip
                        componentsProps={{
                          tooltip: { className: "wide-tooltip w-250" },
                        }}
                        title={
                          <React.Fragment>
                            <Typography color="inherit">
                              Connection Type Code :{" "}
                              {formData?.linked_service?.code}
                            </Typography>
                          </React.Fragment>
                        }
                      >
                        <InfoIcon
                          sx={{
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            right: "-24px",
                            width: "16px",
                          }}
                        />
                      </Tooltip>
                    )}
                  </span>
                </label>
                <Autocomplete
                  fullWidth
                  options={linkedServicesData ?? []}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    linkedServicesData?.find(
                      (option: { id: any }) =>
                        option.id === formData?.linked_service?.id,
                    ) || null
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="linked_service"
                      placeholder="Select..."
                      InputLabelProps={{ shrink: true }}
                      error={!!errors?.linked_service}
                      helperText={errors?.linked_service || ""}
                    />
                  )}
                  renderOption={(params, item) => (
                    <li
                      {...params}
                      key={item?.id || item?.code || item?.name}
                      style={{ paddingTop: "2px", paddingBottom: "2px" }}
                    >
                      <ListItemText>{item?.name}</ListItemText>
                    </li>
                  )}
                  onChange={async (_event, value) => {
                    setFormData((prev: any) => ({
                      ...prev,
                      linked_service: value,
                      connection_key: null,
                    }));
                    validateField("linked_service", value);

                    if (
                      value?.connection_details?.connection_keys?.length > 0
                    ) {
                      try {
                        setIsLoading(true);
                        const fetchedKeys = await getAllConnectionKeys(
                          value.connection_details.connection_keys,
                        );
                        setConnectionKeys(fetchedKeys || []);
                      } catch (fetchError) {
                        console.error(
                          "Error fetching connection keys:",
                          fetchError,
                        );
                        setConnectionKeys([]);
                      } finally {
                        setIsLoading(false);
                      }
                    } else {
                      setConnectionKeys([]);
                    }
                  }}
                  className={`form-control-autocomplete ${
                    errors?.linked_service ? "has-error" : ""
                  }`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <Autocomplete
                  fullWidth
                  options={connectionKeysData || []}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    connectionKeysData?.find(
                      (option: { id: any }) =>
                        option.id === formData?.connection_key,
                    ) || null
                  }
                  onChange={(_event, value) => {
                    setFormData((prev: any) => ({
                      ...prev,
                      connection_key: value?.id,
                    }));
                    validateField("connection_key", value?.id);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="connection_key"
                      label={
                        <span>
                          Connection Key
                          <span className="required-asterisk">*</span>
                        </span>
                      }
                      placeholder="Select..."
                      InputLabelProps={{ shrink: true }}
                      error={!!errors?.connection_key}
                      helperText={errors?.connection_key || ""}
                    />
                  )}
                  className={`form-control-autocomplete ${
                    errors?.connection_key ? "has-error" : ""
                  }`}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">
                  Remote Directory<span className="label-text"></span>
                </label>
                <TextField
                  type="text"
                  name="file_path"
                  onChange={handleFormData}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  className="form-control"
                  value={formData?.file_path || ""}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">File Name</label>
                <TextField
                  type="text"
                  name="file_name"
                  onChange={handleFormData}
                  className="form-control"
                  value={formData?.file_name || ""}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">Output Location</label>
                <TextField
                  type="text"
                  name="output_location"
                  onChange={handleFormData}
                  className="form-control"
                  value={formData?.output_location || ""}
                />
              </Grid>
            </Grid>
          </Box>
          <Box>
            <FlexBetween
              gap="3rem"
              sx={{
                marginTop: "6px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <ButtonComponent
                className="btn-orange mb-2"
                handelClickEvent={onSaveGetSftpList}
              >
                View Files
              </ButtonComponent>
            </FlexBetween>
          </Box>
        </form>
        {tableData.rows?.length > 0 && (
          <FlexBetween>
            <DataTable
              dataColumns={tableData.columns}
              dataRows={tableData.rows}
              loading={isLoading}
              dataListTitle={"SFTP List"}
              className="dataTable no-radius hide-progress-icon"
            />
          </FlexBetween>
        )}
      </Box>
    </>
  );
};

export default SftpList;
